import {
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  NonCancelableCustomEvent,
  SpaceBetween,
  TokenGroup,
  TokenGroupProps,
  StatusIndicator,
  TextContent,
  Spinner,
  BreadcrumbGroupProps,
} from '@amzn/awsui-components-react-v3';
import React, { ReactElement, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import SiteInput from '../../../components/SiteInput';
import { useContainerManagement } from '../useContainerManagement';
import ScanInput from '../../../components/ScanInput/index-v3';
import Divider from '../../../components/Divider';
import {
  ChildContainerInfo,
  ListChildContainerResponse,
} from '../../../services/container-management/types';
import {
  LIST_CHILD_CONTAINER,
  MOVE_CONTAINER,
} from '../../../services/container-management/queries';

export function transformDataForView(childContainerResponse: ListChildContainerResponse): any {
  const children: any = [];
  Object.values(childContainerResponse.childContainers).forEach((child: ChildContainerInfo) => {
    children.push({
      label: child.containerName,
    });
  });
  return children;
}

export default function ContainerMove(props: {
  onBreadcrumbChange: (newBreadcrumbs: BreadcrumbGroupProps.Item[]) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { form } = useContainerManagement();

  const [isTokenFieldHidden, setIsTokenFieldHidden] = useState(true);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [childContainerError, setChildContainerError] = useState<string>('');
  const [isFieldDisabled, setIsFieldDisabled] = useState(false);
  const [moveContainerError, setMoveContainerError] = useState<string>('');

  // query for listChildContainer
  const [listChildContainer, { loading: listChildContainerLoading }] = useLazyQuery(
    LIST_CHILD_CONTAINER,
    {
      fetchPolicy: 'network-only',
      onCompleted: (result) => {
        form.setFieldValue('serials', transformDataForView(result.getContainerHierarchy));
        setIsTokenFieldHidden(false);
        setIsFieldDisabled(true);
        setChildContainerError('');
      },
      onError: (error) => {
        setChildContainerError(error.message);
      },
    }
  );

  // mutation for moveContainer
  const [moveContainer, { loading: movingContainer }] = useMutation(MOVE_CONTAINER, {
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      setMoveContainerError('');
      if (form.values.serials.every((i: TokenGroupProps.Item) => i.label !== form.values.serial)) {
        form.setFieldValue('serials', [{ label: form.values.serial }, ...form.values.serials]);
        form.setFieldTouched('serials');
      }
      form.setFieldValue('serial', '');
      setIsTokenFieldHidden(false);
    },
    onError: (error) => {
      setMoveContainerError(error.message);
    },
  });

  const breadcrumb = [
    {
      text: t('breadcrumb.home'),
      href: '/',
    },
    {
      text: t('Inventory Management'),
      href: '/assets-inventory/',
    },
    {
      text: t('Container Move'),
      href: '/container/move/',
    },
  ];

  useEffect(() => {
    props.onBreadcrumbChange(breadcrumb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateUseCases = () => {
    return form.values.containerAssetTag && form.values.serial;
  };

  // Handles reset button
  const handleSubmit = async (e: any) => {
    setIsSubmitSuccessful(true);
    setTimeout(function () {
      window.location.reload();
    }, 2000);
  };

  const handleAddSerial = async () => {
    if (!validateUseCases()) {
      return;
    }
    if (!form.values.serial || form.errors.serial) {
      return;
    }
    // calling move container
    await moveContainer({
      variables: {
        childContainerName: form.values.serial,
        parentContainerName: form.values.containerAssetTag,
      },
    });
  };

  const handleRemoveSerial = (event: NonCancelableCustomEvent<TokenGroupProps.DismissDetail>) => {
    const [...serials] = form.values.serials;
    serials.splice(event.detail.itemIndex, 1);
    form.setFieldValue('serials', serials);
    setIsTokenFieldHidden(serials.length === 0);
  };

  return (
    <div className="form-max-width">
      <form onSubmit={handleSubmit}>
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                data-test-id="submit-location"
                variant="primary"
                formAction="none"
                className="submitButton"
                onClick={handleSubmit}>
                Done
              </Button>
            </SpaceBetween>
          }
          header={
            <Header
              variant="h1"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <SiteInput />
                </SpaceBetween>
              }>
              {t('Move Container')}
            </Header>
          }>
          <Container>
            <SpaceBetween direction="vertical" size="l">
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <FormField
                    description="Enter the destination container to move to"
                    label={t('Destination container')}
                    errorText={form.touched.containerAssetTag && form.errors.containerAssetTag}>
                    <div style={{ width: '350px' }}>
                      <ScanInput
                        data-test-id="input-serial-number-1"
                        onInput={(value) => {
                          form.setFieldTouched('containerAssetTag', true);
                          form.setFieldValue('containerAssetTag', value, true);
                        }}
                        value={form.values.containerAssetTag}
                        placeholder={t('Scan pallet tag')}
                        disabled={isFieldDisabled}
                        onEnter={(value) => {
                          form.setFieldValue('scannableId', value);
                          setChildContainerError('');
                          listChildContainer({ variables: { containerName: value } });
                        }}
                        name="destinationContainer"
                      />
                      {listChildContainerLoading && <Spinner size="big" />}
                      {childContainerError && (
                        <TextContent>
                          <p>
                            <i className="red-text"> {childContainerError}</i>
                          </p>
                        </TextContent>
                      )}
                    </div>
                  </FormField>
                </div>
                <div>
                  <FormField
                    description="Enter the source containers to move"
                    label={t('Source container')}
                    errorText={form.touched.serial && form.errors.serial}>
                    <div style={{ width: '350px' }}>
                      <ScanInput
                        data-test-id="input-serial-number"
                        onInput={(value) => {
                          form.setFieldTouched('serial', true);
                          form.setFieldValue('serial', value, true);
                        }}
                        onEnter={handleAddSerial}
                        clearOnEnter
                        value={form.values.serial}
                        placeholder={t('Scan tote tag')}
                        name="sourceContainer"
                      />
                      {movingContainer && <Spinner size="big" />}
                      {moveContainerError && (
                        <TextContent>
                          <p>
                            <i className="red-text"> {moveContainerError}</i>
                          </p>
                        </TextContent>
                      )}
                    </div>
                  </FormField>
                </div>
              </ColumnLayout>
              <Divider />
              {!isTokenFieldHidden && (
                <div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label>Containers Moved</label>
                  {isSubmitSuccessful && <StatusIndicator>Success</StatusIndicator>}
                  <TokenGroup
                    data-test-id="list-serials"
                    onDismiss={handleRemoveSerial}
                    items={form.values.serials}
                  />
                </div>
              )}
            </SpaceBetween>
          </Container>
        </Form>
      </form>
    </div>
  );
}
